/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import SDGIcon from '../../sdg-icon/sdg-icon';
import SimpleTooltip from '../../simple-tooltip';
import { getGroup } from '@g17eco/core';
import { capitalise } from '../../../utils';
import { QUESTION } from '../../../constants/terminology';

interface NameCellProps {
  isInGroup: boolean;
  row: any;
}

const getValueTypeText = (valueType: string) => {
  if (valueType === 'valueList') {
    return 'Multiple Choice';
  }

  return capitalise(valueType);
}

const getAlternativeInfo = (id: string, alternatives: { [key: string]: { name: string } }) => {
  const a = [];

  for (const [key, value] of Object.entries(alternatives)) {
    const group = getGroup('standards', key);
    if (group) {
      a.push(`${group.name} - ${value.name}`);
    }
  }
  return a.map((b, i) => <div key={`alt-info-${id}-${i}`}>{b}</div>);
}

export const NameCell = ({ isInGroup, row }: NameCellProps) => {
  const values = row.original;

  const [isOpen, toggleOpen] = React.useState(false);
  const spacer = <i className={'fa fa-blank mr-2'} />;

  const infoRow = (title: string, data: any) => {
    if (!data) {
      return <></>;
    }
    return (
      <div className='mt-2'>
        <div className='strong text-ThemeAccentExtradark'><i className={'fa fa-arrow-circle-right mr-2'} />{title}</div>
        <div className='d-flex text-ThemeTextPlaceholder'>
          <div>{spacer}</div>
          <div>{data}</div>
        </div>
      </div>
    );
  }

  const sdgs = values.tags?.sdg?.map((c: string) => {
    const key = `metric-table-sdg-icon-${values._id}-${c}`;
    return <SimpleTooltip key={key} text={`SDG ${c}`}>
      <SDGIcon height='30' className='mr-2' code={c} />
    </SimpleTooltip>
  });

  return (
    <div className='name-container' onClick={() => toggleOpen(!isOpen)}>
      <div className={`text-truncate ${isInGroup ? '' : 'text-ThemeTextDark'}`}>
        <i className={`fa ${isOpen ? 'fa-caret-down' : 'fa-caret-right'} mr-2`} />
        {values.name}
      </div>
      <div className={`name-container-details ${!isOpen ? 'd-none' : 'd-flex'} pb-3 border-bottom`}>
        <div>{spacer}</div>
        <div>
          {infoRow(QUESTION.CAPITALIZED_SINGULAR, values.valueLabel)}
          {infoRow('Instructions', values.instructions)}
          {infoRow('Value Type', getValueTypeText(values.valueType))}
          {infoRow('Metric Unit', capitalise(values.unitType))}
          {infoRow('Related SDGs', values.tags?.sdg?.length > 0 ? sdgs : undefined)}
          {values.alternatives
            ?
            infoRow('Standards', getAlternativeInfo(values._id, values.alternatives))
            :
            <></>
          }
        </div>
      </div>
    </div>
  );
}
